import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiArrowLeft } from 'react-icons/fi';
import { FaBolt, FaCheckCircle, FaLock, FaMobileAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import useStore from '../store/useStore';

const AuthModal = () => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [name, setName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [nameError, setNameError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [mobileError, setMobileError] = useState('');
  const [otpErrorLocal, setOtpErrorLocal] = useState('');
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  const {
    isAuthModalOpen,
    closeAuthModal,
    sendOtp,
    verifyOtp,
    otpLoading,
    otpSent,
    otpError,
    resetOtpState,
    isAuthenticated,
  } = useStore();

  // Reset inputs when modal is closed
  useEffect(() => {
    if (!isAuthModalOpen) {
      setMobile('');
      setOtp(['', '', '', '']);
      setName('');
      setReferralCode('');
      setNameError('');
      setCountdown(0);
      setMobileError('');
      setOtpErrorLocal('');
    }
  }, [isAuthModalOpen]);

  // Close modal when authenticated
  useEffect(() => {
    if (isAuthenticated && isAuthModalOpen) {
      toast.success('Welcome!');
      setMobile('');
      setOtp(['', '', '', '']);
      setName('');
      setReferralCode('');
      setNameError('');
      setCountdown(0);
      setMobileError('');
      setOtpErrorLocal('');
      closeAuthModal();
    }
  }, [isAuthenticated, isAuthModalOpen, closeAuthModal]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Show error toast
  useEffect(() => {
    if (otpError) {
      toast.error(otpError);
    }
  }, [otpError]);

  if (!isAuthModalOpen) return null;

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(value);
    if (mobileError) setMobileError('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }
    if (name.trim().length < 2) {
      setNameError('Enter your full name');
      return;
    }
    setNameError('');
    if (!mobile) {
      const err = 'Mobile number is required';
      setMobileError(err);
      toast.error(err);
      return;
    }
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(mobile)) {
      const err = 'Please enter a valid 10-digit Indian mobile number';
      setMobileError(err);
      toast.error(err);
      return;
    }
    setMobileError('');
    try {
      await sendOtp(mobile);
      const { testOtp: receivedOtp } = useStore.getState();
      if (receivedOtp) {
        toast(`Your OTP is: ${receivedOtp}`, {
          duration: 10000,
          icon: <FaMobileAlt />,
          style: { background: '#1e293b', color: '#fff', fontWeight: '700', fontSize: '16px', borderRadius: '14px', padding: '14px 20px' },
        });
      } else {
        toast.success(`OTP sent to +91 ${mobile}`, { duration: 5000, icon: <FaMobileAlt /> });
      }
      setCountdown(60);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (_) {}
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (otpErrorLocal) setOtpErrorLocal('');
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (value && index < 3) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setOtp(pasted.split(''));
      otpRefs[3].current?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length !== 4) {
      const err = 'Please enter the complete 4-digit OTP';
      setOtpErrorLocal(err);
      toast.error(err);
      return;
    }
    setOtpErrorLocal('');
    try {
      await verifyOtp(mobile, otpStr, name.trim(), referralCode.trim());
    } catch (_) {}
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setOtp(['', '', '', '']);
    setOtpErrorLocal('');
    try {
      await sendOtp(mobile);
      const { testOtp: receivedOtp } = useStore.getState();
      if (receivedOtp) {
        toast(`Your OTP is: ${receivedOtp}`, {
          duration: 10000,
          icon: <FaMobileAlt />,
          style: { background: '#1e293b', color: '#fff', fontWeight: '700', fontSize: '16px', borderRadius: '14px', padding: '14px 20px' },
        });
      } else {
        toast.success('OTP resent successfully!', { duration: 5000, icon: <FaMobileAlt /> });
      }
      setCountdown(60);
      otpRefs[0].current?.focus();
    } catch (_) {}
  };

  const handleBack = () => {
    resetOtpState();
    setOtp(['', '', '', '']);
    setMobile('');
    setName('');
    setReferralCode('');
    setNameError('');
    setCountdown(0);
    setMobileError('');
    setOtpErrorLocal('');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
      {/* Overlay */}
      <div className="absolute inset-0" onClick={closeAuthModal} />

      {/* Modal */}
      <div className="relative w-full max-w-md transform scale-100 transition-all">
        
        {/* Card */}
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl">
          
          {/* Close button */}
          <button
            onClick={closeAuthModal}
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-lg backdrop-blur-sm transition-all hover:bg-white hover:text-gray-900 hover:shadow-xl"
          >
            <FiX className="h-5 w-5" />
          </button>

          {/* Gradient header */}
          <div className="relative h-24 overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-emerald-600">
            <div className="absolute -left-10 -top-10 h-40 w-40 animate-pulse rounded-full bg-white/10" />
            <div className="absolute -right-10 -bottom-10 h-40 w-40 animate-pulse rounded-full bg-white/10 animation-delay-1000" />
            <div className="relative flex h-full items-center justify-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 shadow-lg backdrop-blur-sm">
                <img src="/kkisandairy.png" alt="KisanFresh" className="h-10 w-10 object-contain" />
              </div>
              <span className="text-lg font-extrabold text-white">Kisan Daily Fresh</span>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 pt-5">
            
            {/* Title */}
            <div className="mb-4 text-center">
              <h2 className="mb-1 text-xl font-bold text-gray-900">
                {otpSent ? 'Verify OTP' : 'Login / Sign Up'}
              </h2>
              <p className="text-sm text-gray-500">
                {otpSent
                  ? `Enter the 4-digit code sent to +91 ${mobile}`
                  : 'Enter your details to continue'}
              </p>
            </div>

            {/* Mobile Step */}
            {!otpSent && (
              <form onSubmit={handleSendOtp} className="space-y-3">

                {/* Name */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Rahul Sharma"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value.replace(/[^a-zA-Z\s]/g, ''));
                      if (nameError) setNameError('');
                    }}
                    className={`w-full rounded-xl border-2 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:bg-white ${
                      nameError ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-primary-500'
                    }`}
                  />
                  {nameError && <p className="mt-1.5 text-xs font-semibold text-red-500">{nameError}</p>}
                </div>

                {/* Mobile */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <div className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 transition-all
                    ${mobileError
                      ? 'border-red-500 bg-red-50/10 focus-within:border-red-500 focus-within:shadow-sm'
                      : 'border-gray-200 bg-gray-50 focus-within:border-primary-500 focus-within:bg-white focus-within:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-2 border-r border-gray-300 pr-3">
                      <span className="text-xl">🇮🇳</span>
                      <span className="text-sm font-semibold text-gray-600">+91</span>
                    </div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="Enter 10-digit number"
                      value={mobile}
                      onChange={handleMobileChange}
                      className="flex-1 bg-transparent text-sm font-medium text-gray-900 placeholder-gray-400 outline-none"
                    />
                    {mobile.length === 10 && !mobileError && (
                      <span className="text-primary-500">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                  </div>
                  {mobileError && (
                    <p className="mt-1.5 text-xs font-semibold text-red-500">{mobileError}</p>
                  )}
                </div>

                {/* Referral Code */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">
                    Referral Code
                    <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. REF123"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium uppercase tracking-widest text-gray-900 outline-none transition-all placeholder:normal-case placeholder:tracking-normal placeholder:text-gray-400 focus:border-primary-500 focus:bg-white"
                    maxLength={20}
                  />
                </div>

                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {otpLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Sending OTP...
                    </span>
                  ) : 'Send OTP'}
                </button>
              </form>
            )}

            {/* OTP Step */}
            {otpSent && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-primary-600"
                >
                  <FiArrowLeft className="h-4 w-4" />
                  Change Number
                </button>

                <div>
                  <label className="mb-3 block text-sm font-semibold text-gray-700">Enter OTP</label>
                  <div className="flex justify-between gap-2" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={otpRefs[i]}
                        type="tel"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        className={`h-12 w-12 rounded-xl border-2 text-center text-xl font-bold outline-none transition-all
                          ${otpErrorLocal
                            ? 'border-red-500 bg-red-50/10 focus:border-red-500'
                            : digit
                              ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                              : 'border-gray-200 bg-gray-50 focus:border-primary-400 focus:bg-white'
                          }`}
                      />
                    ))}
                  </div>
                  {otpErrorLocal && (
                    <p className="mt-2 text-center text-xs font-semibold text-red-500">{otpErrorLocal}</p>
                  )}
                </div>

                <div className="text-center text-sm text-gray-500">
                  Didn't receive the code?{' '}
                  {countdown > 0 ? (
                    <span className="font-semibold text-gray-400">Resend in {countdown}s</span>
                  ) : (
                    <button type="button" onClick={handleResend} disabled={otpLoading}
                      className="font-semibold text-primary-600 hover:underline disabled:opacity-50">
                      Resend OTP
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {otpLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : 'Verify & Continue'}
                </button>
              </form>
            )}

            <p className="mt-4 text-center text-xs text-gray-400">
              By continuing, you agree to our{' '}
              <span className="cursor-pointer text-primary-600 hover:underline">Terms</span>
              {' '}and{' '}
              <span className="cursor-pointer text-primary-600 hover:underline">Privacy Policy</span>
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {[
            { Icon: FaLock, label: 'Secure' },
            { Icon: FaBolt, label: 'Fast' },
            { Icon: FaCheckCircle, label: 'Trusted' },
          ].map(({ Icon, label }) => (
            <div key={label} className="rounded-xl bg-white/80 px-3 py-2 shadow-sm backdrop-blur-sm">
              <Icon className="mx-auto h-4 w-4 text-primary-600" aria-hidden="true" />
              <div className="mt-0.5 text-xs font-medium text-gray-600">{label}</div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default AuthModal;
